/* =============================================================================
   ics-UI SHELL ENTRY - THE DIALOG FAMILY: window + messager + tooltip.
   Bundle entry -> dist-v2/ics-dialogs.min.js, loaded by ALL THREE page shells
   (`_Meta`, `_MobileLayout`, `_AuthMeta`) and by the two Layout=null
   GUProduction device heads. It is NOT the full library: exactly core +
   Registry + WindowStackService/KeyService + the window component
   (js/components/ics-window.js) + the messager composite (js/components/ics-messager.js)
   + the tooltip add-on (js/components/ics-tooltip.js) + the facade bridge +
   auto-mount, and it claims nothing else. Each bundle owns its OWN Registry
   module state - but the INSTANCE key (`Component.KEY`) and the window stack
   are realm-shared by design (Symbol.for), so an element mounted by one bundle
   IS visible to every other: one element, one component instance. That is
   exactly why the tooltip is an ADD-ON under its own instance key rather than
   a second Component - most of its triggers are buttons whose component slot
   the button bundle rightfully owns.

   WHY WINDOW AND MESSAGER SHIP TOGETHER: the messager boxes are composed on the
   window substrate and share its WindowStackService (Escape ordering, the
   one-close-per-ESC guard) and its z-counter. Splitting them would put two
   Window classes and two stacks on one page, and the boxes would stop stacking
   correctly over the windows they interrupt.
   WHY THE TOOLTIP RIDES THE SAME BUNDLE (the numberbox-rides-textbox pattern):
   it is a floating surface of the same family; its Escape-to-dismiss registers
   with the KeyService THIS entry installs, so the key seam stays in one
   module; and this is the one bundle every shell already links, so the card
   reaches every screen with no new script tag on any shell.

   WHAT IT DOES ON A PAGE
   1. Mounts `[data-ics="window"]` markup - the one authored form of a window.
   2. `$.fn.window` becomes a DISPATCHER: an own-name / ics-mounted receiver goes
      to the component's bridge; every other receiver - including empty sets -
      goes to the implementation that held the name before us, untouched. That
      implementation's statics (defaults/methods/parseOptions) are carried onto
      the dispatcher, because such callers read them through `$.fn.window` at
      call time. On a page where nothing else defines the name, ours answers
      everything.
   3. `$.fn.icsWindow` + IcsWindowClose / icsWindowCloseTop
      / UnselectAllRowsOfATable / OpenWindowWithPost are OURS on the page
      (last write wins, re-asserted in the ready queue). `oActiveWindows` is
      defined CLAIM-IF-FREE.
   4. THE ELEVEN MESSAGER GLOBALS ARE OURS: IcsMsg / IcsAlert / IcsMsgBox /
      IcsToast / IcsToastOk|Err|Warn|Info / IcsConfirm / IcsPrompt /
      IcsModalDialog -
      assigned at boot and re-asserted in the ready queue (last write wins over
      any other script that re-declares one of the names). One module defines
      them for every shell. `$.messager` is NOT a name this bundle claims.
      `IcsMsg(key[, tokens])` is THE dictionary call: this bundle registers the
      92 generated product messages into the realm-shared i18n catalog, so the
      dictionary is resolvable from every bundle on the page while its payload
      is carried only here.
      IcsToast* is the ONE toaster: its cards stack bottom-right and every toast
      is recorded in the recall list.
   5. Publishes `Ics.components.Window` + `Ics.components.Tooltip` +
      `Ics.services.windowStack` (+ the messager bridge under `Ics.messager`
      and the recall list under `Ics.toastHistory` - the shell's 'info' panel
      reads it) ADDITIVELY onto an existing minimal `Ics`, or publishes a
      minimal `Ics` when none exists.
   5b. Mounts `[data-ics-tip]` markup as the tooltip ADD-ON (its own instance
      key - it co-exists with the button/field component that owns the
      element) and exposes `$.fn.icsTooltip` through the add-on's OWN bridge
      (attachTooltipFacade; the generated bridges resolve through the
      component slot and would answer with the wrong widget). Last write wins,
      re-asserted in the ready queue. `$.fn.tooltip` is NEVER touched, wrapped
      or read - the NAME RULE (facade/register.js) lists that plain name as
      one another script on the page already defines, so the ics-prefixed
      name is the only call name.
   6. Escape closes the topmost window: the central KeyService dispatcher is
      installed on the page (capture phase). An OPEN MESSAGER BOX takes the key
      first - the guard below consumes Escape while `hasOpenBox()` so the box's
      own capture handler answers it (Esc = No / dismiss) instead of the stack
      closing it as a plain window; the one-close-per-ESC guard keeps a page's
      own `keydown -> icsWindow('close')` handler from closing a second window
      on the same keypress. IcsModalDialog frames are plain windows: Escape
      closes the topmost one and its fnDone runs.
   CSS: the shells link lib-v2/css/components/ics-window.css,
   lib-v2/css/components/ics-messager.css and lib-v2/css/components/ics-tooltip.css
   raw (tokens + ics-icons are already on the shell). NO stylesheet is
   self-injected by this bundle - sheet and script ship together or not at all.
   ============================================================================= */
import { Component } from './core/component.js';
import { Registry } from './services/registry.js';
import { makeBridge } from './facade/register.js';
import { mount, unmount, installAutoMount, installMutationMount } from './facade/mount.js';
import { EventBinder } from './core/event-binder.js';
import { WindowStackService, KeyService } from './services/windowstack.js';
import { Window, attachWindowFamily, attachWindowGlobals, installPanelDispatch, WINDOW_GLOBALS, icsWindowCloseTop } from './components/ics-window.js';
import { messagerBridge, attachMessagerBridge, attachMessager, hasOpenBox, toastHistory } from './components/ics-messager.js';
import { MessageCatalogService } from './i18n/l10n/localisation-map.js';   // published as Ics.i18n; the messager module registers the product dictionary at import
import { Tooltip, attachTooltipFacade } from './components/ics-tooltip.js';   // registers 'Tooltip' ([data-ics-tip], facade:false) on THIS bundle's Registry

const OWN = '[data-ics="window"]';

/**
 * Escape closes the topmost window: the central KeyService dispatcher (capture
 * phase) is installed once per page. Ordering per keypress: an open messager
 * box takes the key (consumed + marked on the stack's one-close-per-ESC guard,
 * so neither the dispatcher nor a page's own `icsWindow('close')` handler
 * closes a window under it - the box's own capture handler, registered later,
 * then answers Esc ≡ No / dismiss); otherwise the stack closes its topmost
 * entry and the guard makes every later handler on that keypress a no-op.
 * Idempotent.
 */
export function installEscape() {
  if (KeyService.isInstalled()) return false;
  KeyService.register((e, key) => {
    if (key !== 'Escape' || !hasOpenBox()) return false;
    WindowStackService.noteEscConsumed();
    return true;
  }, { keys: ['Escape'] });
  KeyService.install(new EventBinder(document));
  return true;
}

/* narrow the registration for THIS bundle: own-name markup only, no alias name */
const entry = Registry.get('Window');
if (entry) {
  entry.selector = OWN;
  entry.claimName = null;
}

/** an element the component owns: already mounted by us, or declared with the own name */
export function isOurs(el) {
  if (!el || el.nodeType !== 1) return false;
  if (Component.get(el) instanceof Window) return true;
  return !!(el.matches && el.matches(OWN));
}

/**
 * Install the page-level `$.fn.window` dispatcher over whatever holds the name
 * (nothing, on a page where this bundle is the only implementation). Idempotent.
 * @returns {boolean} installed (or already installed)
 */
export function installWindowDispatch($) {
  if (!$ || !$.fn) return false;
  const cur = $.fn.window;
  if (typeof cur === 'function' && cur._icsWindowDispatch) return true;
  const ours = makeBridge($, 'Window', Window, OWN);
  const foreign = typeof cur === 'function' ? cur : null;
  const dispatch = function () {
    /* empty set: leave it to the implementation that held the name, where there is one */
    if (this.length === 0) return (foreign || ours).apply(this, arguments);
    return (!foreign || isOurs(this[0])) ? ours.apply(this, arguments) : foreign.apply(this, arguments);
  };
  if (foreign) Object.assign(dispatch, foreign);      // defaults / methods / parseOptions - read via $.fn.window at call time
  dispatch._icsWindowDispatch = true;
  dispatch._icsForeign = foreign;
  dispatch._icsBridge = ours;
  $.fn.window = dispatch;
  return true;
}

const g = globalThis;
if (typeof document !== 'undefined') {
  attachMessagerBridge(g);                                    // the ten messager globals: ours (before any view script)
  const $ = g.jQuery || g.$;
  if ($ && $.fn) {
    attachMessager($);                                        // $.messager: claim-if-free (spec Q1; the boot hook was authored but unwired until the vendor's 2026-09-02 final delete freed the name and the dialogs gate caught the gap)
    installWindowDispatch($);                                 // $.fn.window: ours for own-name/mounted, the prior holder otherwise
    installPanelDispatch($);                                  // $.fn.panel on an own window -> the window ("a window is a panel" idiom); else the prior panel handler
    attachWindowFamily($);                                    // $.fn.icsWindow: ours (last write wins)
    attachWindowGlobals(g);                                   // the 5 window globals: ours
    attachTooltipFacade($);                                   // $.fn.icsTooltip: the add-on's OWN bridge - the ONLY call name (never $.fn.tooltip)
    if (!('oActiveWindows' in g)) g.oActiveWindows = [];      // the page-global window stack NAME resolves (claim-if-free)
    installEscape();                                          // Escape closes the topmost window (messager boxes first)
    $(() => {                                                 // ready-queue re-assert (same contract as index.js)
      installWindowDispatch($);
      installPanelDispatch($);
      attachWindowFamily($);
      attachWindowGlobals(g);
      attachMessagerBridge(g);
      attachTooltipFacade($);
    });
  }
  installAutoMount();                                         // ready-queue mount (before per-view glue)
  installMutationMount();                                     // post-ready injections (any path)
}

const IcsDialogsEntry = {
  version: '2.2.0-dialogs',
  Component,
  registry: Registry,
  components: { Window, Tooltip },
  /* THE key dispatcher is published, not just the stack. This entry is the one
     that INSTALLS the document listener on a shell, so its copy of the service
     is the page's; a component in another bundle that needs to answer a key
     reaches THIS instance (`Ics.services.key`), or it would register with a
     copy nothing dispatches to. A date field claiming Escape while its panel
     is open is exactly that case. */
  services: { windowStack: WindowStackService, key: KeyService },
  messager: messagerBridge,
  /* the message dictionary, for code that needs the TEXT without showing a box
     (a grid cell, a document title, a report header) - `Ics.i18n.t(key, tokens)`
     and `Ics.i18n.entry(key)` for the whole entry */
  i18n: MessageCatalogService,
  toastHistory,
  WINDOW_GLOBALS, icsWindowCloseTop, installWindowDispatch, installPanelDispatch, installEscape, isOurs, hasOpenBox,
  mount, unmount,
};
if (!g.Ics) g.Ics = IcsDialogsEntry;                          // no namespace yet: publish the minimal one
else {                                                        // another entry published one: add to it, never replace
  g.Ics.components = Object.assign(g.Ics.components || {}, { Window, Tooltip });
  g.Ics.services = Object.assign(g.Ics.services || {}, {
    windowStack: g.Ics.services && g.Ics.services.windowStack || WindowStackService,
    key: g.Ics.services && g.Ics.services.key || KeyService,
  });
  if (!g.Ics.messager) g.Ics.messager = messagerBridge;
  if (!g.Ics.i18n) g.Ics.i18n = MessageCatalogService;         // the catalog store itself is realm-shared, so any copy answers
  if (!g.Ics.toastHistory) g.Ics.toastHistory = toastHistory;
  if (!g.Ics.hasOpenBox) g.Ics.hasOpenBox = hasOpenBox;       // the box guard stays readable on every shell
}
export default IcsDialogsEntry;
